import { LoaderCircleIcon } from "lucide-react";
import React from "react";
import type { ReactNode } from "react";
import { Button as BaseButton } from "../../shad/ui/button";
import { cx } from "class-variance-authority";

type ButtonProps = Parameters<typeof BaseButton>[0] & {
  startIcon?: React.ComponentType<{ className?: string }>;
  endIcon?: React.ComponentType<{ className?: string }>;
  loading?: boolean;
  full?: boolean;
  children?: ReactNode;
  classNames?: { content?: string; spinner?: string; icon?: string };
};

export const Button = ({
  startIcon: StartIcon,
  endIcon: EndIcon,
  loading = false,
  disabled = false,
  full = false,
  children,
  className,
  variant,
  size,
  classNames,
  ...props
}: ButtonProps) => {
  const isDisabled = disabled || loading;

  return (
    <BaseButton
      {...props}
      variant={variant}
      size={size}
      disabled={isDisabled}
      className={cx("relative", full && "w-full", className)}
    >
      {loading && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
          <LoaderCircleIcon
            className={`animate-spin ${classNames?.spinner || ""}`}
          />
        </div>
      )}
      {/* Main Content: Opacity 0 when loading (preserves width/height) */}
      <span
        className={cx(
          "flex items-center gap-2",
          loading ? "opacity-0" : "opacity-100",
        )}
      >
        {StartIcon && (
          <StartIcon className={classNames?.icon} data-icon="inline-start" />
        )}
        {children}
        {EndIcon && (
          <EndIcon className={classNames?.icon} data-icon="inline-end" />
        )}
      </span>
    </BaseButton>
  );
};

Button.displayName = "Button";
